import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import { timeAgo } from "@/lib/api";
import { StatusChip } from "./RegulationRadar";
import { PolicyHead } from "./PolicyIntelligence";
import { sectorTrack } from "./SectorHero";

const PRIORITY_CLS: Record<string, string> = {
  critical: "text-[#F87171]", high: "text-[#F59E0B]", medium: "text-gray-300", low: "text-gray-500",
};

const COLS = "md:grid md:grid-cols-[minmax(0,2.4fr)_0.8fr_1fr_0.9fr_0.7fr_0.9fr_0.8fr_0.8fr_28px] md:items-center md:gap-3";

const Expanded = ({ r }: { r: any }) => (
  <div className="grid md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 mt-1 mb-3"
    data-testid={`tracker-expanded-${r.slug}`}>
    <div className="min-w-0 space-y-3">
      {r.summary && (
        <div>
          <p className="font-mono text-[8px] font-bold uppercase tracking-[0.22em] text-gray-500">Summary</p>
          <p className="text-gray-300 text-[12.5px] leading-relaxed mt-1">{r.summary}</p>
        </div>
      )}
      {r.why_it_matters && (
        <div>
          <p className="font-mono text-[8px] font-bold uppercase tracking-[0.22em] text-[#A78BFA]">Why It Matters</p>
          <p className="text-gray-300 text-[12.5px] leading-relaxed mt-1">{r.why_it_matters}</p>
        </div>
      )}
      <Link to={`/regulation/${r.slug}`} onClick={() => sectorTrack("regulation_click", r.slug, "policy")}
        className="group inline-flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-wide text-[#00E5FF] hover:text-white transition-colors">
        Open regulation <ArrowRight className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" />
      </Link>
    </div>
    <div className="min-w-0 space-y-3">
      {(r.timeline || []).length > 0 && (
        <div>
          <p className="font-mono text-[8px] font-bold uppercase tracking-[0.22em] text-gray-500">Timeline</p>
          <div className="mt-1.5 space-y-1.5">
            {r.timeline.map((t: any, i: number) => (
              <p key={i} className="flex gap-2.5 text-[11.5px] leading-snug">
                <span className="font-mono text-gray-500 shrink-0">{t.date}</span>
                <span className="text-gray-300">{t.event}</span>
              </p>
            ))}
          </div>
        </div>
      )}
      {r.latest_linked && (
        <div>
          <p className="font-mono text-[8px] font-bold uppercase tracking-[0.22em] text-[#00E5FF]">Latest Linked Coverage</p>
          <Link to={`/article/${r.latest_linked.slug}`}
            className="group block text-gray-300 text-[12px] leading-snug mt-1 hover:text-white transition-colors">
            ↳ {r.latest_linked.title}
          </Link>
        </div>
      )}
    </div>
  </div>
);

const Row = ({ r, open, toggle }: { r: any; open: boolean; toggle: () => void }) => (
  <div className="border-b border-white/[0.06] last:border-0">
    <button onClick={toggle} data-testid={`tracker-row-${r.slug}`} aria-expanded={open}
      className={`w-full text-left py-3 ${COLS} hover:bg-white/[0.02] transition-colors rounded-md px-1 -mx-1`}>
      <span className="text-white font-semibold text-[13px] leading-snug md:truncate md:pr-2">{r.title}</span>
      <span className="inline-flex gap-1 mt-1.5 md:mt-0">
        {(r.codes || []).map((c: string) => (
          <span key={c} className="rounded border border-white/[0.16] bg-black/40 px-1.5 py-0.5 font-mono text-[8.5px] font-bold tracking-[0.14em] text-gray-200">{c}</span>
        ))}
      </span>
      <span className="hidden md:block font-mono text-[9.5px] uppercase tracking-wide text-gray-400 truncate">
        {(r.agencies || []).join(", ") || "—"}
      </span>
      <span className="inline-flex mt-1.5 md:mt-0 mr-2 md:mr-0"><StatusChip status={r.status} /></span>
      <span className={`hidden md:block font-mono text-[9px] font-bold uppercase tracking-wide ${PRIORITY_CLS[r.priority] || "text-gray-500"}`}>
        {r.priority || "—"}
      </span>
      <span className="hidden md:block font-mono text-[9.5px] text-gray-400">{r.effective_date || "—"}</span>
      <span className="hidden md:block font-mono text-[9.5px] text-gray-500">{timeAgo(r.updated_at)}</span>
      <span className="font-mono text-[9px] uppercase tracking-wide mt-1 md:mt-0 inline-block">
        {r.linked_articles > 0
          ? <span className="text-[#00E5FF]">{r.linked_articles} linked</span>
          : <span className="text-gray-600 hidden md:inline">—</span>}
      </span>
      <ChevronDown aria-hidden className={`hidden md:block h-3.5 w-3.5 text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <Expanded r={r} />}
  </div>
);

/* REGULATION TRACKER (§14-16): exact structured comparison — the Radar orients,
   this module is precise. Rows expand into the record's stored intelligence;
   clicks route only to real destinations (/regulation/{slug} detail pages). */
export const RegulationTrackerModule = ({ items }: { items: any[] }) => {
  const [open, setOpen] = useState<string | null>(null);
  if (!items?.length) return null;
  return (
    <section data-testid="policy-tracker" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-12">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <PolicyHead title="Regulation Tracker" testid="policy-tracker-head"
            desc="Every structured record tracked by the NewsLayer desk — exact fields, confirmed coverage only." />
        </div>
        <Link to="/regulation-tracker" data-testid="tracker-full-link"
          onClick={() => sectorTrack("regulation_tracker_click", "lower", "policy")}
          className="group font-mono text-[10px] font-bold uppercase tracking-wide text-gray-400 hover:text-[#00E5FF] transition-colors whitespace-nowrap shrink-0">
          Full tracker <ArrowRight className="inline h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" />
        </Link>
      </div>
      <div className="rounded-xl border border-white/[0.08] bg-[#0a0b10] px-4 sm:px-5 py-2">
        <div className={`hidden ${COLS} py-2.5 border-b border-white/[0.08]`}>
          {["Regulation", "Jurisdiction", "Regulator", "Status", "Priority", "Effective", "Updated", "Coverage", ""].map((h, i) => (
            <span key={i} className="font-mono text-[8px] font-bold uppercase tracking-[0.18em] text-gray-500">{h}</span>
          ))}
        </div>
        {items.map((r) => (
          <Row key={r.id} r={r} open={open === r.id}
            toggle={() => setOpen(open === r.id ? null : r.id)} />
        ))}
      </div>
    </section>
  );
};
